import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, Check, CalendarDays, CheckCircle2, Clock, Gift, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { THC } from "@/lib/tankHealthCheck";
import { CampaignHeader } from "@/components/tank-health-check/CampaignHeader";
import { CampaignFooter } from "@/components/tank-health-check/CampaignFooter";

export default function TankWaterHealthCheckConfirmed() {
  const { state, search } = useLocation();
  useSEO({ title: "Your Health Check is booked | AWARE Water Testing", noindex: true });
  const [booking, setBooking] = useState(state?.booking || null);
  const [offerToken, setOfferToken] = useState(state?.booking?.lab_offer_token || "");

  useEffect(() => {
    const ref = new URLSearchParams(search).get("ref");
    if (booking || !ref) return;
    api.get(`/tank-water-health-check/bookings/${ref}`)
      .then(({ data }) => setBooking(data.booking))
      .catch(() => setBooking(null));
  }, [search, booking]);

  /* The offer token is encrypted at rest — ask the server to rebuild the
     secure link when the customer returns to this page. */
  useEffect(() => {
    if (!booking?.reference || !booking.lab_bonus_eligible || offerToken) return;
    api.get(`/tank-water-health-check/bookings/${booking.reference}/offer-link`)
      .then(({ data }) => setOfferToken(data.token))
      .catch(() => setOfferToken(""));
  }, [booking, offerToken]);

  return (
    <div style={{ backgroundColor: "#FFFFFF", color: THC.body }} data-testid="thc-confirmed-page">
      <CampaignHeader onBook={() => { window.location.href = "/tank-water-health-check#book"; }}
        onNav={() => { window.location.href = "/tank-water-health-check"; }} />

      <section className="mx-auto max-w-[1440px] px-[18px] md:px-7 xl:px-10 py-9 md:py-14">
        <div className="max-w-[760px] mx-auto text-center">
          <CheckCircle2 className="w-[68px] h-[68px] mx-auto" style={{ color: THC.blue }} aria-hidden="true" />
          <h1 className="mt-3 font-extrabold uppercase leading-[1.05] text-[31px] md:text-[42px]"
            style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
            YOUR HEALTH CHECK IS BOOKED
          </h1>
          <p className="mt-2.5 text-[16px] md:text-[18px]" style={{ color: THC.body }}>
            We’ve received your Complimentary Tank Water Health Check booking.
          </p>
        </div>

        {booking && (
          <div className="mt-7 max-w-[760px] mx-auto rounded-[16px] border p-[18px] md:p-6 grid gap-4 md:grid-cols-3"
            style={{ borderColor: THC.border, backgroundColor: THC.faint, boxShadow: THC.shadow }}
            data-testid="thc-confirmation-card">
            <Detail icon={MapPin} label="PROPERTY" value={booking.property_address} testId="thc-confirm-address" />
            <Detail icon={CalendarDays} label="DATE" value={booking.date_display} testId="thc-confirm-date" />
            <Detail icon={Clock} label="BOOKED ARRIVAL WINDOW"
              value={booking.arrival_window_display} testId="thc-confirm-time" />
          </div>
        )}

        {booking?.arrival_window_display && (
          <div className="mt-4 max-w-[760px] mx-auto rounded-[13px] border p-4 md:p-5"
            style={{ borderColor: THC.border, backgroundColor: "#FFFFFF" }}
            data-testid="thc-confirm-arrival-window">
            <p className="font-bold text-[16px] md:text-[17px]" style={{ color: THC.navy }}>
              Your booked arrival window is {booking.arrival_window_display}.
            </p>
            <p className="mt-1.5 text-[14px] leading-[1.5]" style={{ color: THC.body }}>
              {booking.arrival_window_notice}
            </p>
            <p className="mt-1.5 text-[14px] leading-[1.5]" style={{ color: THC.body }}>
              We’ll also send you a reminder by text and email the day before your booking.
            </p>
          </div>
        )}

        <div className="mt-5 max-w-[760px] mx-auto rounded-[13px] p-4 md:p-5 flex items-start gap-4"
          style={{ backgroundColor: THC.yellow }} data-testid="thc-confirm-owner-strip">
          <span className="shrink-0 w-[44px] h-[44px] rounded-full bg-white grid place-items-center">
            <AlertTriangle className="w-6 h-6" style={{ color: THC.yellowInk }} aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-black uppercase text-[21px] md:text-[25px] leading-none"
              style={{ color: THC.navy, fontFamily: "'Outfit',system-ui,sans-serif" }}>
              OWNER MUST BE PRESENT
            </h2>
            <p className="mt-1.5 font-semibold text-[15.5px] md:text-[17px] leading-[1.4]" style={{ color: THC.navy }}>
              Please make sure the property owner is available so a Pristine Water Systems
              technician can show you the
              findings and explain any recommended next steps.
            </p>
          </div>
        </div>

        {booking && (
          <div className="mt-5 max-w-[760px] mx-auto rounded-[14px] border p-[18px] md:p-5"
            style={{ borderColor: THC.border, backgroundColor: THC.faint }}
            data-testid="thc-confirm-provider">
            <h2 className="font-extrabold uppercase text-[17px] md:text-[19px]" style={{ color: THC.navy }}>
              YOUR ONSITE HEALTH CHECK
            </h2>
            <p className="mt-1.5 text-[15px] md:text-[16px] leading-[1.45]" style={{ color: THC.body }}>
              Your complimentary Tank Water Health Check will be conducted by Pristine Water
              Systems, AWARE Water Testing’s onsite service partner.
            </p>
          </div>
        )}

        {booking?.lab_order_reference && (
          <div className="mt-4 max-w-[760px] mx-auto rounded-[14px] border p-[18px] md:p-5"
            style={{ borderColor: THC.border, backgroundColor: "#EAF7FD" }}
            data-testid="thc-confirm-sample-collection">
            <h2 className="font-extrabold uppercase text-[17px] md:text-[19px]" style={{ color: THC.navy }}>
              AWARE SAMPLE COLLECTION INCLUDED
            </h2>
            <p className="mt-1.5 text-[15px] md:text-[16px] leading-[1.45]" style={{ color: THC.body }}>
              The attending Pristine Water Systems technician will collect your AWARE
              laboratory sample during the visit.
            </p>
          </div>
        )}

        {booking?.lab_bonus_eligible && !booking?.lab_order_reference && (
          <div className="mt-5 max-w-[760px] mx-auto rounded-[14px] p-[18px] md:p-6"
            style={{ backgroundColor: THC.navy }} data-testid="thc-confirm-bonus">
            <div className="flex items-start gap-4">
              <Gift className="w-[38px] h-[38px] text-white shrink-0" aria-hidden="true" />
              <div>
                <h2 className="text-white font-extrabold uppercase text-[21px] md:text-[24px]">
                  YOU’VE SECURED YOUR 20% AWARE LAB BONUS
                </h2>
                <p className="mt-1.5 text-white text-[15.5px] md:text-[17px] leading-[1.45]">
                  Because you booked through this campaign, you’ve unlocked
                  {" "}{booking.lab_bonus_percent || 20}% off any AWARE laboratory water-test package.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-[12px] bg-white/10 p-[16px] md:p-5">
              <h3 className="text-white font-extrabold uppercase text-[17px] md:text-[19px]">
                WANT TO ADD YOUR TEST NOW?
              </h3>
              <p className="mt-1.5 text-white text-[15px] md:text-[16px] leading-[1.45]">
                Order your discounted test before your appointment and the attending
                Pristine Water Systems technician can collect the sample during your Tank
                Water Health Check.
              </p>
              <ul className="mt-2.5 space-y-1.5">
                {["One visit", "No separate sample drop-off",
                  "Sample collected during your scheduled Health Check",
                  "Laboratory results and easy-to-understand summary",
                  "Water-correction recommendations if required"].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-white text-[14.5px] md:text-[15.5px]">
                    <Check className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              {offerToken && (
                <Link to={`/tank-water-health-check/lab-offer?token=${encodeURIComponent(offerToken)}`}
                  data-testid="thc-lab-offer-cta"
                  className="mt-4 inline-flex items-center justify-center h-[54px] px-7 rounded-[8px] text-[16px] md:text-[17px] font-extrabold text-white transition-transform hover:-translate-y-0.5"
                  style={{ backgroundColor: THC.blue }}>
                  GET MY 20% OFF LAB TEST
                </Link>
              )}
              <p className="mt-2 text-white/80 text-[13.5px]">
                Already ordered your test? You’re all set.
              </p>
            </div>
          </div>
        )}

        {booking && (
          <p className="mt-5 text-center text-[14px]" style={{ color: THC.muted }} data-testid="thc-confirm-reference">
            Booking reference {booking.reference}
          </p>
        )}
      </section>

      <CampaignFooter />
    </div>
  );
}

function Detail({ icon: Icon, label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 text-[12.5px] font-bold uppercase tracking-wide" style={{ color: THC.muted }}>
        <Icon className="w-4 h-4" style={{ color: THC.blue }} aria-hidden="true" /> {label}
      </div>
      <div className="mt-1 text-[17px] font-bold leading-snug" style={{ color: THC.navy }}>{value || "—"}</div>
    </div>
  );
}
